import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { getScopeRole } from '@/services/current-context';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import { ArrowLeft, RefreshCw, TriangleAlert, Upload } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { Modal } from '@/components/ui/modal';
import { Spinner } from '@/components/ui/spinner';
import { useDirtyStateBlocker } from '@/hooks/useDirtyStateBlocker';
import { useScrollDirection } from '@/hooks/useScrollDirection';
import { Typography } from '@/components/ui/typography';
import {
  AssetRequestModal,
  AssetViewModal,
  useAssetCart
} from '@/components/ui/carousel';
import type { ServiceType } from '@/components/ui/carousel';
import type { ImageAssetType } from '@/components/ui/carousel/carousel.types';
import { showAlert } from '@/slice/AlertSlice';
import { Variants } from '@/types/common';
import UploadModal from './upload/UploadModal';
import type { UploadContext } from './upload/uploadAssetService';
import { useFileDropTarget } from './upload/useFileDropTarget';
import {
  resolveDropTarget,
  UPLOAD_TARGET_NOUN,
  type UploadTarget
} from './upload/resolveDropTarget';
import {
  addUploaded,
  clearAllSelections,
  clearDirtyFlags,
  setPhotoPool,
  setSearchQuery
} from './slice/ImageAssetSlice';
import {
  bulkSelection,
  deletableSelection,
  hasCheckedAssets,
  stageableSelection,
  type PhotoPool
} from './model/assetViewScope';
import { FLOOR_PLAN_CAPTION, isFloorPlan } from './floor-plan/floorPlan';
import { isDeliveredOrderStatus } from '../orders/table-cell/model/orderCell';
import {
  canDeleteAssets,
  canUseEditSuite,
  canUseVirtualStaging,
  canUploadAssets,
  canUploadFloorPlans,
  canUploadStagedAssets,
  isInternalAssetTeamUser
} from './assetFeatureFlags';
import SyncStatusChip from './edit/SyncStatusChip';
import { UnsyncedLeaveDialog } from './edit/UnsyncedLeaveDialog';
import {
  shouldShowUnsyncedBadge,
  shouldShowSyncStateNote,
  visibleDirtyCount
} from './edit/unsyncedBadge';
import StagingModal from './stage-with-ai/StagingModal';
import StagedAssetsGrid from './stage-with-ai/StagedAssetsGrid';
import StagedLeadingActions from './stage-with-ai/StagedLeadingActions';
import StagedMenuItems from './stage-with-ai/StagedMenuItems';
import { pickThumbnailUrl } from './model/pickThumbnailUrl';
import {
  PMS_SYNC_CACHE_KEY,
  useGetAssetTrashQuery,
  useSyncAssetsMutation,
  useTriggerWebhookMutation,
  WEBHOOK_PUSH_CACHE_KEY
} from './services/assetApi';
import { useAssetTrash } from './trash/useAssetTrash';
import DeleteConfirmDialog, {
  type DeletePreviewPhoto
} from './trash/DeleteConfirmDialog';
import TrashGrid from './trash/TrashGrid';
import TrashSummary from './trash/TrashSummary';

import PhotosLeadingActions from './property-asset-view/assets-action-bar/PhotosLeadingActions';
import PhotosMenuItems from './property-asset-view/assets-action-bar/PhotosMenuItems';
import RequestSupportMenuItem from './property-asset-view/assets-action-bar/RequestSupportMenuItem';
import AssetServicePills from './property-asset-view/assets-action-bar/AssetServicePills';
import AssetsControlBar from './property-asset-view/assets-control-bar/AssetsControlBar';
import AssetsSearchField from './property-asset-view/assets-control-bar/AssetsSearchField';
import AssetsSelectionBar from './property-asset-view/assets-selection-bar/AssetsSelectionBar';
import type { AssetTabId, AssetTabItem } from './property-asset-view/assets-control-bar/assetTabs';
import AssetsImageGrid from './property-asset-view/asset-images/AssetsImageGrid';
import ZillowAssets from './zillow3dAssets/ZillowAssets';
import DownloadAsset from './download-assets/DownloadAsset';
import type { DownloadScope } from './download-assets/downloadScope';
import {
  buildImageFileName,
  buildZipNameBase
} from './download-assets/downloadNaming';
import SyncModal from './sync/SyncModal';
import { syncButtonTitle } from './sync/syncButtonTitle';
import ShareControl from './share/ShareControl';
import { shareGate } from './share/shareGate';
import {
  canSharePhotoset,
  canSync as canSyncForRole,
  hasCaptionSortToolbarButton
} from '@/util/roles';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { DESKTOP_BREAKPOINT_QUERY } from '@/config/ui.constants';
import { useReleaseFlag } from '@/hooks/useReleaseFlag';
import FailedAssetSync from './failed-asset-sync/FailedAssetSync';
import type { SyncFailure } from './failed-asset-sync/syncFailure';
import { usePropertyAssets, type AssetsEntry } from './hooks/usePropertyAssets';
import { useWorkspaceAccount } from './hooks/useWorkspaceAccount';
import { failedSyncActions, pausedNoticeActions } from './pmsNoticeActions';
import OrderServiceModal from './order-service-modal/OrderServiceModal';
import { PhotosetNotFound } from './PhotosetNotFound';
import { usePhotosetVisibility } from './visibility/usePhotosetVisibility';
import PhotosetVisibilityBanner from './visibility/PhotosetVisibilityBanner';
import PhotosetVisibilityDialog from './visibility/PhotosetVisibilityDialog';
import MakeVisibleNotifyDialog from './visibility/MakeVisibleNotifyDialog';
import CaptionSortButton from './property-asset-view/assets-action-bar/CaptionSortButton';
import CaptionSortMissingCaptionsDialog from './edit/CaptionSortMissingCaptionsDialog';
import { useCaptionSortPrompt } from './edit/useCaptionSortPrompt';
import { PhotosetUnavailable } from './visibility/PhotosetUnavailable';
import {
  PropertyLoadError,
  PropertyNotFound
} from '../properties/property-shared';
import { ROUTES } from '../../config/routes';
import { initiateOrgSwitch } from '../../services/org-data';
import type { Asset } from '../../types/asset.types';

type AssetTab = AssetTabId;

// What the download modal operates on, captured when it opens: the default
// selection AND which hidden photos its "Include hidden photos?" opt-in may
// add. Captured per source (DV-2740) — from the staged bar only hidden STAGED
// photos qualify; the old page-wide hidden list leaked hidden Photos-tab
// photos into a staged download.
type DownloadModalTarget = DownloadScope;

interface PropertyAssetManagementPageProps {
  /** DV-2849: which shell this workspace was entered from. Defaults to the
   *  in-account /home routes. The /internal/global-orders/assets routes pass
   *  'global-orders'. See usePropertyAssets for what it changes. */
  entry?: AssetsEntry;
}

const PropertyAssetWorkspace = ({ entry = 'account' }: PropertyAssetManagementPageProps) => {
  // DV-2849: the not-found/load-error heroes below must never route back
  // through the /home properties list (entity-scoped) on the internal entry
  // — that's the guard bounce-and-force-switch chain this route exists to
  // avoid. backAction/backTooltip already carry the internal-entry behavior.
  const isInternalEntry = entry === 'global-orders';
  const navigate = useNavigate();
  const dispatch = useDispatch();
  // Drives the fullscreen viewer's photo list so it matches the grid (DV-2820).
  const showHidden = useSelector(
    (state: RootState) => state.imageAsset.showHidden
  );
  const searchQuery = useSelector(
    (state: RootState) => state.imageAsset.searchQuery
  );
  // DV-3714: whether Share is offered here at all. The hook reads the
  // photoset's own order only for its gate.
  const shareReleased = useReleaseFlag('photosetShare');
  const canShare = canSharePhotoset(getScopeRole());
  const {
    propertyMeta,
    workspaceOrderUUID,
    workspaceOrderStatus,
    photosetOrderStage,
    photosetIsAnotherOrders,
    isNotFound,
    isLoadError,
    retryPropertyFetch,
    routePhotosetMissing,
    assetLoadError,
    retryAssetLoad,
    noVisiblePhotoset,
    currentOrgName,
    loading,
    zillow3d,
    lastSynced,
    handlePmsSyncSuccess,
    pmsSource,
    isPaused,
    pmsStatusPending,
    hasPhotoset,
    photoSetOptions,
    selectedPhotosetId,
    selectedPhotoset,
    handlePhotoSetChange,
    markPhotosetSynced,
    markPhotosetUnsynced,
    setPhotosetVisibility,
    setPhotosetPublicToken,
    imageAssets,
    selectedImages,
    assetServiceList,
    showSyncButton,
    getDownloadScope,
    getStagedDownloadSelection,
    getSyncSelection,
    backAction,
    backTooltip,
    propertyUUID
  } = usePropertyAssets(entry, { shareOffered: shareReleased && canShare });

  // DV-2849 PR 2: whose account this workspace is about. The session's on
  // /home; the property's own on the internal workspace, read without
  // switching into it. One answer, passed to the header, the sync-state
  // callout, Sync and Download, so none of them can disagree.
  const account = useWorkspaceAccount(entry, propertyMeta);
  // DV-2861: whether the client can see the selected photoset. The
  // property's own account is who gains or loses access, on both entries.
  const visibility = usePhotosetVisibility({
    photoset: selectedPhotoset,
    loading,
    accountName: propertyMeta?.entity ?? null,
    onApplied: setPhotosetVisibility
  });
  // Read by the upload modal's onDiscardIncomplete, which can fire seconds after the modal closed
  // (discard retries), by when the user may be on another photoset.
  const selectedPhotosetIdRef = useRef(selectedPhotosetId);
  selectedPhotosetIdRef.current = selectedPhotosetId;
  const share = shareGate({
    loading,
    selectedPhotosetId,
    // The photoset's own order, which asset-service judges when minting.
    orderStage: photosetOrderStage,
    visibility,
    canShare
  });
  const shareShown = shareReleased && share.show;
  const visibilityMenuItem = visibility.available
    ? { visible: visibility.visible, onOpen: visibility.openDialog }
    : undefined;
  // DV-3345 (spec §4.4): which buttons the PMS notices show, from the entry, the
  // role and the property's account. /home returns today's behavior.
  const noticeInput = {
    entry,
    role: getScopeRole(),
    entityUUID: propertyMeta?.entityUUID ?? null,
    accountName: account.name,
    vendor: pmsSource
  };
  const failedActions = failedSyncActions(noticeInput);
  const pausedActions = pausedNoticeActions(noticeInput);

  // Decided 2026-09-18: while any photo is checked, every view filter is
  // locked — the search field, the photoset select and the Show Hidden Photos
  // kebab item. That is what keeps the selection a subset of what is
  // displayed, which is why the counts and the actions below can share one
  // pool. The kebab TRIGGER is deliberately not locked (sub-lg it also carries
  // Request Support), nor is Show Image Details, which changes what each card
  // prints rather than which cards render.
  const filtersLocked = hasCheckedAssets(imageAssets);

  // The unsynced badge tracks caption/reorder/hide edits and Connect uploads,
  // channel by channel. The direct PMS photo sync can only deliver SOME of
  // those: new photo upload, Buildium/AppFolio (not Rentvine) delete/restore
  // of hidden synced photos, and the photo order (DV-2799) — but caption
  // edits of already-synced photos never reach any PMS (the caption is frozen
  // into the filename at first upload). The legacy webhook snapshot carries every
  // badge-raising edit. shouldShowUnsyncedBadge lights the webhook side on
  // any pending edit and the PMS side only on a PMS-deliverable one, each
  // gated on the entity having that channel — see unsyncedBadge.ts. The
  // Sync button (below) is deliberately NOT gated the same way (its
  // modal explains the disabled states itself).
  const syncStateInput = {
    hasWebhooks: account.hasWebhookEndpoints,
    isPmsSynced: showSyncButton,
    hasBackendUnsynced: selectedPhotoset?.hasUnsyncedChanges ?? false,
    hasBackendPmsUnsynced: selectedPhotoset?.hasUnsyncedPmsChanges ?? false,
    dirtyCount: imageAssets.filter((img) => img.dirty).length,
    pmsDirtyCount: imageAssets.filter((img) => img.dirtyPms).length
  };
  // DV-3519: Internal Post-Processing gets no Sync — no button, no
  // sync-state chip, no leave prompt. See canSync in util/roles.
  const canSync = canSyncForRole(getScopeRole());
  const showSyncStateNote =
    canSync && canUseEditSuite() && shouldShowSyncStateNote(syncStateInput);

  // DV-2750: pulse the Sync button once when the user makes a new
  // deliverable edit on this page — a nudge that there is now something to
  // sync. Keyed to the session-local deliverable dirty count INCREASING,
  // never to the badge state itself: the badge also flips when the
  // photoset's persisted backend flags arrive after load, and that data
  // arrival must not pulse (it isn't "a change was just made"). The pulse
  // class self-clears on animationend so a later edit re-triggers the
  // one-shot animation.
  const isUnsynced = shouldShowUnsyncedBadge(syncStateInput);
  const deliverableDirtyCount = visibleDirtyCount(syncStateInput);
  const prevDirtyCountRef = useRef(deliverableDirtyCount);
  const [syncButtonPulse, setSyncButtonPulse] = useState(false);
  useEffect(() => {
    const prev = prevDirtyCountRef.current;
    prevDirtyCountRef.current = deliverableDirtyCount;
    if (deliverableDirtyCount > prev) setSyncButtonPulse(true);
  }, [deliverableDirtyCount]);

  // In-flight state of the Sync modal's two sync channels, observed
  // via shared fixedCacheKeys (the mutations are triggered inside
  // SyncAssets / WebhooksTab). Drives the callout's spinning refresh icon
  // for exactly the duration of the request.
  const [, { isLoading: pmsSyncInFlight }] = useSyncAssetsMutation({
    fixedCacheKey: PMS_SYNC_CACHE_KEY
  });
  const [, { isLoading: webhookPushInFlight }] = useTriggerWebhookMutation({
    fixedCacheKey: WEBHOOK_PUSH_CACHE_KEY
  });
  const isSyncing = pmsSyncInFlight || webhookPushInFlight;

  // DV-2750 leave guard: session-local dirty edits only, and channel-aware
  // like the callout (visibleDirtyCount) — warn only about edits this
  // entity's lit sync channel(s) can actually deliver, so the guard and the
  // callout always agree ("if we warn you, syncing will fix it"). Using the
  // raw dirtyCount here would fire the warning on a PMS-only entity even
  // after a successful sync clears just dirtyPms (webhook-only edits like
  // captions aren't PMS-deliverable and would still count), and would
  // fire on a no-channel entity that has no callout at all. Persisted
  // backend unsynced flags (hasUnsyncedChanges) survive across visits and
  // would nag on every navigation even when this user changed nothing — only
  // warn when THIS session created unsynced, deliverable work.
  // DV-3519: and never for a role that cannot sync — the prompt's only remedy
  // is Sync.
  // DV-3421: `survivesReload` — these edits are saved, only not yet synced, so
  // a cross-tab reload must not wait on them.
  const leaveBlocker = useDirtyStateBlocker(
    canSync && deliverableDirtyCount > 0,
    { survivesReload: true }
  );

  const [searchParams, setSearchParams] = useSearchParams();
  // Initialize from the URL so refresh / share / deep-link (e.g. the Recent
  // Assets 3D icon) lands on the requested tab.
  const [activeTab, setActiveTab] = useState<AssetTab>(() => {
    const tab = searchParams.get('tab');
    if (tab === 'floorPlan') return 'floorPlan';
    if (tab === 'zillow') return 'zillow';
    if (tab === 'staged') return 'staged';
    if (tab === 'trash') return 'trash';
    return 'photos';
  });

  // The Zillow tab depends on a zillow3d asset that is fetched asynchronously and
  // can resolve after this mounts. Derive the effective tab instead of correcting
  // activeTab in an effect: a ?tab=zillow request stays pending while the fetch is
  // in flight, then activates.
  //
  // DV-2475: it must NOT silently degrade to Photos once the fetch has settled with
  // no asset. The property list's 3D icon is driven by property-service's has3DTour
  // flag, which is set when a Zillow asset is created but never cleared when one is
  // deleted — so it routinely outlives the asset. Falling back to Photos made that
  // look like the wrong page had loaded. Stay on the Zillow tab and say plainly that
  // there is no asset.
  //
  // The `!loading` term is belt-and-braces, not a live race: usePropertyAssets sets
  // zillow3d and flips loading in the same load(), and the whole tab area is behind a
  // spinner while loading, so today zillow3d is always settled by the time this
  // renders. The guard keeps the invariant local — if the zillow fetch is ever split
  // out of that load(), an empty state still can't flash before the asset arrives.
  // Tab visibility is DATA-DRIVEN ONLY, matching showStagedTab / canDelete below — it depends on
  // the selected photoset's data, never on which tab is active. Keying it off the active tab is
  // what made the visible tab set differ by entry path (arrive via the property list's 3D icon and
  // the tab appeared; arrive on Photos and it did not, for the same property), which QA flagged.
  //
  // A ?tab=zillow deep link whose selected photoset has no tour simply lands on Photos, silently.
  // Per Product that is correct, not a degradation: the page shows the latest photoset and older
  // ones are reached through the photoset dropdown, so "no tour on the latest set" is the honest
  // answer. The property list's 3D icon is property-level and can therefore point at a property
  // whose latest photoset has no tour — that mismatch belongs to the icon, not to this page.
  const isZillowActive = activeTab === 'zillow' && !!zillow3d;

  // Virtual Staging tab: always available to edit-suite users (it now hosts
  // upload/download for staged photos); for everyone else it appears once
  // staged photos exist — pre-existing behavior. Same degrade-gracefully
  // pattern as Zillow: a ?tab=staged deep link that doesn't currently
  // qualify falls back to Photos content without losing the URL.
  const photoCount = imageAssets.filter((img) => !img.hidden).length;
  // Counts ALL staged photos, hidden included (DV-2740): the staged grid always
  // shows hidden staged photos (dimmed), so the badge agrees with the grid. The
  // staged action bar's selection math still excludes hidden — see
  // useStagedActions/StagedLeadingActions.
  const stagedCount = imageAssets.filter((img) => img.staged).length;
  // DV-2820: internal roles always have the tab (it is their workspace, empty
  // or not); everyone else sees it only once there is staged work to look at,
  // so a customer with no staged photos gets no empty internal-looking tab.
  // Role, not origin — internal users keep the tab in prod after the ungate.
  //
  // A customer's trigger counts only NON-hidden staged photos, matching what
  // StagedAssetsGrid will actually show them: hiding a staged photo is how
  // internal staff park unapproved AI output, so a property whose only staged
  // work is hidden must not surface a tab that then renders empty (or, before
  // the grid was filtered, exposed that work-in-progress).
  const isAssetTeamUser = isInternalAssetTeamUser();
  // DV-3537: Internal PP gets Caption Sort as a toolbar button where every
  // other role sees the service pills (they lose the pills); other internal
  // users keep the kebab item. Every entry point runs the missing-caption
  // check first. Role gate mirrors the server's (INTERNAL_ADMIN /
  // INTERNAL_POST_PROCESSING, 403 otherwise) — an affordance, not the
  // authorization boundary.
  //
  // Which of the two PP gets is decided from the SAME media query that
  // decides whether AssetsControlBar renders the desktop trailing slot at all,
  // not from CSS lg:hidden on the item: Tailwind's lg is 64rem and follows the
  // browser's default font size, the JS query is 1024px, and at a non-16px
  // default the two disagree — leaving PP with neither control (PR #1204).
  const captionSortPrompt = useCaptionSortPrompt();
  const isDesktop = useMediaQuery(DESKTOP_BREAKPOINT_QUERY);
  const captionSortAsButton = hasCaptionSortToolbarButton(
    getScopeRole()
  );
  const captionSortControlDisabled =
    captionSortPrompt.sorting || !captionSortPrompt.canCaptionSort;
  const captionSortMenuItem =
    isAssetTeamUser && !(captionSortAsButton && isDesktop)
      ? {
          onSelect: captionSortPrompt.requestCaptionSort,
          disabled: captionSortControlDisabled,
          // PP's toolbar button carries no badge, so neither does its mirror.
          internalBadge: !captionSortAsButton
        }
      : undefined;
  // DV-2861: a customer whose property has photosets, none of them visible to
  // them. Internal users are shown every set, so for them an empty list is
  // not a hidden one. No loading term: the grid area renders only once
  // loading ends, and the hook resets the flag at the start of every load.
  const photosetUnavailable =
    !isAssetTeamUser && hasPhotoset && noVisiblePhotoset;

  // DV-2820 pre-delivery upload gate. Upload went client-facing in the same
  // release that made this page reachable for states it was never reachable in
  // before, and two of those states are harmful for a customer:
  //
  //  - NO PHOTOSET: the upload endpoint takes photosetId as OPTIONAL, so the
  //    asset lands with an empty photosetUUIDs: in no set, so no photoset
  //    view shows it after a reload, and the customer has no way to remove it
  //    (trash is internal-only, and a trashed set-less asset cannot be
  //    recovered — it has no origin set). Reachable
  //    today: the orders table links "Manage Assets" on any delivered order,
  //    with no hasPhotoset check. The gate reads the LOADED set, not
  //    `hasPhotoset`: the property flag stays true when the customer's
  //    photosets read comes back empty (DV-2861 — the only set is hidden from
  //    the client) or fails silently, and either way nothing is selected for
  //    the upload to land in.
  //  - NOT DELIVERED: uploading into a shoot still in flight. Rare today
  //    (photosets appear at delivery) but about to be common — internal
  //    photoset creation will make hasPhotoset true well before delivery.
  //
  // Internal roles keep upload in every state; pre-delivery access is the whole
  // point of their tooling. Null orderStatus (router-state seed, before the
  // property fetch lands) reads as not-delivered, so this starts closed.
  //
  // DV-3521: the stage is the OPENED order's when the workspace was opened for
  // one (the orders table's Manage Assets), the same order the upload is
  // tagged with, so the gate and the tag cannot name different orders: a
  // customer opening an undelivered order X on a property whose latest order
  // is delivered gets no upload. Only an entry that named no order reads the
  // property's latest order's stage; there, a property with delivered photos
  // and a newly booked shoot loses upload until that order completes. The
  // right signal is a per-photoset delivered state, which internal photoset
  // creation will need anyway.
  const canUploadHere =
    canUploadAssets() &&
    (isAssetTeamUser ||
      (selectedPhotosetId !== null &&
        isDeliveredOrderStatus(workspaceOrderStatus)));
  const customerVisibleStagedCount = imageAssets.filter(
    (img) => img.staged && !img.hidden
  ).length;
  const showStagedTab = isAssetTeamUser || customerVisibleStagedCount > 0;
  const isStagedActive = activeTab === 'staged' && showStagedTab;

  // DV-3735 Floor Plan tab: the Photos grid narrowed to floor plans (a photo
  // captioned so — see floor-plan/floorPlan.ts). Same visibility rule as
  // Virtual Staging: internal roles always have it, empty or not; a customer
  // sees it only once there is a floor plan they can see, so a hidden one
  // surfaces no tab. The count follows the Photos badge (not hidden), which is
  // what the grid shows with Show Hidden off.
  const floorPlanCount = imageAssets.filter(
    (img) => isFloorPlan(img) && !img.hidden
  ).length;
  const showFloorPlanTab = isAssetTeamUser || floorPlanCount > 0;
  const isFloorPlanActive = activeTab === 'floorPlan' && showFloorPlanTab;
  const canUploadFloorPlansHere = canUploadFloorPlans();

  // Delete/trash (DV-2524) needs BOTH the role/origin gate and photoset
  // context: the trash endpoints resolve entity scope through the photoset, so
  // the order-only page path (a property with no photoset) would 400. Computed
  // once here and passed down — no child re-derives it.
  const canDelete = canDeleteAssets() && hasPhotoset && !!selectedPhotosetId;
  // The page owns the listing query so the tab's count badge and TrashGrid
  // share one cache subscription.
  const {
    data: trashedAssets = [],
    // isLoading, NOT isFetching: all three trash mutations invalidate the
    // AssetTrash tag, so isFetching goes true on every post-mutation refetch and
    // would unmount the whole grid for a spinner after each restore or purge —
    // defeating busyAssetUUID's per-row disable. isLoading is true only when the
    // cache key has no data yet, which still covers a photoset switch.
    isLoading: trashLoading,
    isError: trashError,
    refetch: refetchTrash
  } = useGetAssetTrashQuery(selectedPhotosetId ?? '', { skip: !canDelete });
  // Same degrade-gracefully rule as Zillow/staged: a ?tab=trash deep link that
  // doesn't qualify falls back to Photos content without rewriting the URL.
  const isTrashActive = activeTab === 'trash' && canDelete;

  // Photos is the fallback tab, so it is active whenever no other tab is —
  // including a ?tab=… deep link whose tab didn't qualify.
  const isPhotosActive =
    !isZillowActive && !isStagedActive && !isTrashActive && !isFloorPlanActive;
  // Photos and Floor Plan share one set of controls and one grid; the pool is
  // the only difference between them. The slice carries it to every reader of
  // the view filters; this page is its one writer. A layout effect, so the
  // pass that renders the new tab never paints with the old pool.
  //
  // The reset on unmount is this effect's own cleanup, never a passive
  // effect's: the page is keyed by property, and a passive cleanup of the old
  // page runs AFTER the new page's layout effect, so property A -> B on
  // ?tab=floorPlan would reset B's pool to 'all' under a Floor Plan tab
  // (PR #1245 review). Layout cleanups of a removed page run first.
  const photoPool: PhotoPool = isFloorPlanActive ? 'floorPlans' : 'all';
  useLayoutEffect(() => {
    dispatch(setPhotoPool(photoPool));
    return () => {
      dispatch(setPhotoPool('all'));
    };
  }, [dispatch, photoPool]);
  // The active view filters, as model/assetViewScope reads them. The handlers
  // below take their pools from that module — the same one the selection bar's
  // counts come from — so a count and the action it labels cannot disagree.
  const viewFilters = useMemo(
    () => ({ showHidden, searchQuery, pool: photoPool }),
    [showHidden, searchQuery, photoPool]
  );

  // The effective tab — a ?tab=… deep link that doesn't qualify (e.g.
  // ?tab=zillow on a photoset with no tour) renders Photos content, so the
  // tab widget must show Photos as active too, not the raw (unqualified)
  // activeTab it was requested with. Every other tab is active only when it
  // is the requested one, so the requested tab is the effective one unless
  // Photos is standing in for it.
  const effectiveTab: AssetTabId = isPhotosActive ? 'photos' : activeTab;

  // AssetsControlBar's tab list -- availability is DATA-DRIVEN ONLY (see the
  // isZillowActive/showStagedTab/canDelete derivations above), never keyed to
  // which tab happens to be active. Keying it off the active tab is what
  // made the visible tab set differ by entry path, which QA flagged.
  const tabs: AssetTabItem[] = useMemo(() => {
    const items: AssetTabItem[] = [
      { id: 'photos', label: 'Photos', count: photoCount }
    ];
    if (showFloorPlanTab) {
      items.push({ id: 'floorPlan', label: 'Floor Plan', count: floorPlanCount });
    }
    // Hidden when the SELECTED photoset has no tour. DV-2475's AC allowed "disabled or
    // hidden"; Product chose hidden, so a photoset without a tour carries no dead tab.
    // Per Product, the page shows the latest photoset and older ones are reached via
    // the photoset dropdown — so this tab appearing and disappearing as you switch
    // photosets is the intended model, not an inconsistency.
    if (zillow3d) {
      items.push({ id: 'zillow', label: 'Zillow 3D' });
    }
    if (showStagedTab) {
      items.push({
        id: 'staged',
        label: 'Virtual Staging',
        // Count must match what the tab actually renders for this user, or a
        // customer sees a badge of 3 over a grid of 1 — the hidden staged
        // photos leaking as a number.
        count: isAssetTeamUser ? stagedCount : customerVisibleStagedCount
      });
    }
    if (canDelete) {
      items.push({ id: 'trash', label: 'Trash', count: trashedAssets.length });
    }
    return items;
  }, [
    photoCount,
    showFloorPlanTab,
    floorPlanCount,
    zillow3d,
    showStagedTab,
    isAssetTeamUser,
    stagedCount,
    customerVisibleStagedCount,
    canDelete,
    trashedAssets.length
  ]);

  const { trashPhotos, recoverPhoto, purgePhoto, busyAssetUUID } =
    useAssetTrash({
      photosetId: selectedPhotosetId,
      onPhotosetUnsynced: markPhotosetUnsynced
    });

  // Product decision (PR #1057 review Note 1): selection is per-tab-visit.
  // Leaving a tab clears every checkmark so a bulk action on the new tab can't
  // pick up selections made on the old one.
  const leaveTab = useCallback(() => {
    dispatch(clearAllSelections());
    // DV-2856: search scope follows the active tab. Carrying a query across a
    // tab switch would silently filter a set it was never typed against.
    dispatch(setSearchQuery(''));
    setSearchExpanded(false);
  }, [dispatch]);

  const showTab = useCallback(
    (tab: AssetTab) => {
      setActiveTab(tab);
      setSearchParams(
        (prev) => {
          if (tab === 'photos') prev.delete('tab');
          else prev.set('tab', tab);
          return prev;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const selectTab = useCallback(
    (tab: AssetTab) => {
      // Re-clicking the tab on screen is not a switch and must not wipe an
      // in-progress selection — measured against the EFFECTIVE tab, since
      // Photos stands in for a requested tab that doesn't qualify.
      if (tab !== effectiveTab) leaveTab();
      showTab(tab);
    },
    [effectiveTab, leaveTab, showTab]
  );

  // DV-3735: a customer's Floor Plan tab exists only while they can see a
  // floor plan, and the captions and hides that decide it are theirs to edit.
  // Once the page has loaded, a Floor Plan request that doesn't qualify is
  // dropped rather than left pending like the other tabs': pending, it would
  // re-open the tab the moment any photo was captioned "Floor Plan", carrying
  // over a selection and search made on Photos. Photos was standing in for
  // it, so this is a tab switch and clears like one.
  const floorPlanRequestLapsed =
    !loading && activeTab === 'floorPlan' && !showFloorPlanTab;
  useEffect(() => {
    if (!floorPlanRequestLapsed) return;
    leaveTab();
    showTab('photos');
  }, [floorPlanRequestLapsed, leaveTab, showTab]);

  const [downloadTarget, setDownloadTarget] =
    useState<DownloadModalTarget | null>(null);
  const [showImageViewModal, setShowImageViewModal] = useState(false);
  const [imageViewIndex, setImageViewIndex] = useState(0);
  // Which list the open viewer pages through. Set at open time (the source tab
  // can't change underneath an open modal).
  const [viewerScope, setViewerScope] = useState<
    'photos' | 'floorPlans' | 'staged'
  >('photos');
  const [showAssetRequestModal, setShowAssetRequestModal] = useState(false);
  // DV-2856 (D8): sub-lg, the search field takes over the control row. The page
  // owns the flag because two siblings need it — AssetsControlBar swaps the
  // row's contents, and the scroll-collapse below must stand down while it's
  // open (a row the user is typing into must not slide away under their finger).
  const [searchExpanded, setSearchExpanded] = useState(false);
  const [pickedService, setPickedService] = useState<ServiceType | null>(null);
  // Sub-lg services→order back flow (QA round 5 follow-up, Product request).
  // The sheet's open state lives here (not in AssetsSelectionBar) because the
  // Order Service sheet's back arrow reopens it from another subtree. The
  // origin flag gates that arrow to sheet-initiated picks only — the desktop
  // pills path has no sheet to go back to. `orderServiceExiting` keeps the
  // order sheet mounted with open=false through its slide-down, so the back
  // transition (order sheet down, services sheet up) actually plays; the
  // modal's onExited unmounts it when the animation finishes.
  const [servicesSheetOpen, setServicesSheetOpen] = useState(false);
  const [pickedFromServicesSheet, setPickedFromServicesSheet] = useState(false);
  const [orderServiceExiting, setOrderServiceExiting] = useState(false);
  const [showSyncModal, setShowSyncModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [syncFailure, setSyncFailure] = useState<SyncFailure | null>(null);

  // Sync and Share each act on one photoset. When the page starts loading
  // another (browser Back/Forward: the switcher sits behind the dialog), close
  // Sync rather than let it follow the page to a set it was not opened on.
  useEffect(() => {
    if (loading) setShowSyncModal(false);
  }, [loading]);
  // The Share modal is mounted by the Share control, which unmounts whenever
  // it stops showing (a photoset switch, or its release flag turning off for
  // this role). Its open state must not outlive the control, or the drop zone
  // stays blocked with no dialog up and the modal reopens by itself when the
  // control comes back.
  useEffect(() => {
    if (!shareShown) setShowShareModal(false);
  }, [shareShown]);

  // Callback-ref state (not a ref): the scroll column mounts behind the
  // loading gate, so useScrollDirection needs a re-render to attach its
  // listener once the element exists. Drives the sub-lg control-row
  // hide-on-scroll-down / reveal-on-scroll-up (QA round 4 follow-up).
  const [scrollColumn, setScrollColumn] = useState<HTMLDivElement | null>(
    null
  );
  const scrollDirection = useScrollDirection(scrollColumn);

  // Which bar opened the upload modal: uploads from the Virtual Staging bar
  // are persisted staged=true and land in that tab; uploads from the Floor
  // Plan bar are captioned "Floor Plan" (DV-3735).
  const [uploadTarget, setUploadTarget] = useState<UploadTarget | null>(null);

  const handleOpenUpload = useCallback(() => setUploadTarget('photos'), []);
  const handleOpenStagedUpload = useCallback(
    () => setUploadTarget('staged'),
    []
  );
  const handleOpenFloorPlanUpload = useCallback(
    () => setUploadTarget('floorPlan'),
    []
  );
  // The upload the active Photos-shaped tab offers, or none.
  const photosTabUpload = isFloorPlanActive
    ? { allowed: canUploadFloorPlansHere, open: handleOpenFloorPlanUpload }
    : { allowed: canUploadHere, open: handleOpenUpload };
  const openPhotosTabUpload = photosTabUpload.allowed
    ? photosTabUpload.open
    : undefined;

  // DV-3521: the open photoset is one Connect created for a different order
  // than the one this workspace was opened for (usePropertyAssets decides;
  // the upload tag follows the set there, as asset-service would file it).
  // Internal entry only: the remedy it names, Link or Create Photoset, lives
  // on Global Orders, and the customer entry has nothing to offer instead.
  const showOtherOrderNotice =
    isInternalEntry && !loading && photosetIsAnotherOrders;

  // DV-3521: an upload is tagged with the workspace's order (the one it was
  // opened for; the property's latest only when the entry named none, which
  // is what every upload used to carry -- and how order 947831's photos came
  // to be tagged as another order's). The hook owns the rule, so the tag and
  // the no-photoset grid cannot disagree. asset-service re-checks either way.
  const uploadContext: UploadContext | null =
    propertyUUID && workspaceOrderUUID
      ? {
          propertyUUID,
          orderUUID: workspaceOrderUUID,
          photosetId: selectedPhotosetId ?? undefined,
          ...(uploadTarget === 'staged' ? { staged: true } : {}),
          ...(uploadTarget === 'floorPlan' ? { caption: FLOOR_PLAN_CAPTION } : {})
        }
      : null;

  const handleUploaded = (images: ImageAssetType[]) =>
    dispatch(addUploaded(images));

  // Cart-awareness: if the picked service is already in the cart for exactly
  // the selected photos, reopen that line in edit mode instead of a blind add
  // (which would silently overwrite it). Fetch eagerly so the lookup is ready
  // the moment a service is picked.
  const { getExactCartLineForSelection } = useAssetCart(true);

  // The viewer must page through exactly what the grid shows, or arrow and
  // thumbnail navigation resurface photos the grid is hiding. DV-2820: that
  // means following the Show Hidden toggle, not a role gate — pre-ungate an
  // internal user with the toggle OFF still met hidden photos in the viewer.
  // For a customer, Show Hidden reveals only their OWN hidden photos: a
  // hidden STAGED photo is internal unapproved WIP (see AssetsImageGrid) and
  // stays out of the viewer list just as it stays out of the grid.
  // Index safety: click handlers remap by photoUUID, so the filtered list's
  // shifted indexes are never used to address the original array.
  const viewerImages = useMemo(
    () =>
      imageAssets.filter((img) =>
        img.hidden
          ? showHidden && (isAssetTeamUser || !img.staged)
          : true
      ),
    [imageAssets, showHidden, isAssetTeamUser]
  );

  // The Floor Plan tab's viewer pages through the floor plans it shows.
  const floorPlanViewerImages = useMemo(
    () => viewerImages.filter(isFloorPlan),
    [viewerImages]
  );

  // Staged-scoped viewer list (DV-2740): opening the viewer from the Virtual
  // Staging tab must page through staged photos only — hidden staged INCLUDED,
  // because the staged grid always shows them.
  // DV-2820: mirrors StagedAssetsGrid's own filter — a customer must not be
  // able to arrow-navigate into hidden (unapproved) staged photos that the
  // grid is withholding from them. Internal users keep the full staged list.
  const stagedViewerImages = useMemo(
    () =>
      imageAssets.filter(
        (img) => img.staged && (isAssetTeamUser || !img.hidden)
      ),
    [imageAssets, isAssetTeamUser]
  );

  // The list the open viewer pages through, by the tab that opened it.
  const viewerImagesByScope: Record<typeof viewerScope, ImageAssetType[]> =
    useMemo(
      () => ({
        photos: viewerImages,
        floorPlans: floorPlanViewerImages,
        staged: stagedViewerImages
      }),
      [viewerImages, floorPlanViewerImages, stagedViewerImages]
    );
  // The viewer scope the Photos-shaped grid opens. The click below finds the
  // photo's index in this scope's list, and the viewer shows the same list,
  // so the index can't address a different photo.
  const gridViewerScope = isFloorPlanActive ? 'floorPlans' : 'photos';

  const handleImageClick = useCallback(
    (index: number) => {
      // Click sources pass the ORIGINAL imageAssets index; the viewer list
      // may be hidden-filtered (see viewerImages), so remap by photoUUID.
      const clicked = imageAssets[index];
      if (!clicked) return;
      const viewerIndex = viewerImagesByScope[gridViewerScope].findIndex(
        (img) => img.photoUUID === clicked.photoUUID
      );
      if (viewerIndex === -1) return;
      setViewerScope(gridViewerScope);
      setImageViewIndex(viewerIndex);
      setShowImageViewModal(true);
    },
    [imageAssets, viewerImagesByScope, gridViewerScope]
  );

  const handleStagedImageClick = useCallback(
    (index: number) => {
      // Staged cards pass the ORIGINAL imageAssets index; remap by photoUUID
      // into the staged-only viewer list (same rule as handleImageClick).
      const clicked = imageAssets[index];
      if (!clicked) return;
      const viewerIndex = stagedViewerImages.findIndex(
        (img) => img.photoUUID === clicked.photoUUID
      );
      if (viewerIndex === -1) return;
      setViewerScope('staged');
      setImageViewIndex(viewerIndex);
      setShowImageViewModal(true);
    },
    [imageAssets, stagedViewerImages]
  );

  // Card icon / viewer download: the options modal scoped to that one photo.
  // Single-image downloads never render the hidden opt-in, so no hidden pool.
  const handleDownloadPhoto = useCallback(
    (photoUUID: string) =>
      setDownloadTarget({
        selection: { photoUUIDs: [photoUUID] },
        hiddenPhotoUUIDs: []
      }),
    []
  );

  // Stage with AI (DV-2252): internal-only, both internal roles (DV-2751 —
  // this previously read INTERNAL_ADMIN-only). Role alone since DV-2820: the
  // non-prod origin gate is gone, so staging runs in prod for internal users.
  // Gated by the per-capability check, not canUseEditSuite(), so loosening
  // the combined gate must not drag every staging affordance with it.
  const canOpenStagingModal = canUseVirtualStaging();
  const [stagingPhotos, setStagingPhotos] = useState<ImageAssetType[] | null>(
    null
  );

  // Both delete confirmations (DV-2524): trash mode is fed from the grid's
  // working set, purge mode from the trash listing.
  const [deleteTarget, setDeleteTarget] = useState<ImageAssetType[] | null>(
    null
  );
  const [purgeTarget, setPurgeTarget] = useState<Asset | null>(null);

  // Files handed to UploadModal by a page-level drop. Cleared with uploadTarget.
  const [pendingDropFiles, setPendingDropFiles] = useState<File[] | null>(null);

  // Every dialog this page can have open. A page-level drop stands down while
  // any of them is up: the overlay must not cover a dialog in use, and a stray
  // drop must not stack a second upload onto one already in progress.
  const anyModalOpen =
    uploadTarget !== null ||
    downloadTarget !== null ||
    showImageViewModal ||
    showAssetRequestModal ||
    pickedService !== null ||
    showSyncModal ||
    showShareModal ||
    syncFailure !== null ||
    stagingPhotos !== null ||
    deleteTarget !== null ||
    purgeTarget !== null ||
    visibility.dialog !== null ||
    captionSortPrompt.dialog !== null;

  // Per-capability, not canUseEditSuite(): upload and staging are diverging,
  // and the Virtual Staging tab is already visible to non-admins whenever the
  // property has staged photos.
  const dropUploadTarget = resolveDropTarget({
    isZillowActive,
    isStagedActive,
    isFloorPlanActive,
    isTrashActive,
    canUpload: canUploadHere,
    canUploadStaged: canUploadStagedAssets(),
    canUploadFloorPlans: canUploadFloorPlansHere
  });

  const handleFileDrop = useCallback(
    (files: File[]) => {
      // NOT dead code: useFileDropTarget captures `enabled` in its effect
      // closure, while `onDrop` is read from a ref on every render. The
      // window listener is native (not a React synthetic event), so React's
      // passive-effect flush for discrete events never runs for it — a stale
      // `enabled: true` closure can still fire this callback against a
      // freshly-null dropUploadTarget (e.g. the active tab just changed).
      // This guard is the only thing that catches that window.
      if (!dropUploadTarget) return;
      setUploadTarget(dropUploadTarget);
      setPendingDropFiles(files);
    },
    [dropUploadTarget]
  );

  // Match the button path exactly: while the page is loading it renders only a
  // spinner with no action bar, so there is no Upload affordance to mirror — and
  // selectedPhotosetId is still null, so a drop would create an asset with no
  // photoset back-link. Also covers the photoset-switch window, where the load
  // effect re-runs while selectedPhotosetId still holds the previous id.
  // Nor behind the internal entry's heroes (DV-2849 PR 3): selectedPhotosetId
  // there can still name the set shown before a failed switch.
  const { isDragActive } = useFileDropTarget({
    enabled:
      !loading &&
      !routePhotosetMissing &&
      !assetLoadError &&
      !!dropUploadTarget &&
      !!uploadContext &&
      !anyModalOpen,
    onDrop: handleFileDrop
  });

  // Drop affordance, scoped to the active tab's grid region rather than the
  // viewport. The window listener still owns the drop — so there are no dead
  // zones and a stray drop is still suppressed rather than navigating the tab
  // away — but the visual points at where the photos will actually land.
  //
  // Styling matches the app's canonical upload dropzone, `border-info
  // bg-info-soft` in ui/bulk-file-import.tsx. The fill is translucent so the
  // photos underneath stay visible; that box is empty, ours is not.
  //
  // pointer-events-none is load-bearing: an interactive overlay would generate
  // its own dragenter/dragleave churn against the window listener.
  const dropOverlay =
    isDragActive && dropUploadTarget ? (
      <div
        className="pointer-events-none absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed border-info bg-info-soft/80"
        aria-hidden="true"
      >
        <Upload className="h-8 w-8 text-info" />
        <Typography as="p" variant="heading-lg" className="font-medium text-info">
          Drop {UPLOAD_TARGET_NOUN[dropUploadTarget]} to Upload
        </Typography>
        {dropUploadTarget === 'floorPlan' && (
          <Typography as="p" variant="body" className="text-info">
            Each one is captioned “{FLOOR_PLAN_CAPTION}”
          </Typography>
        )}
      </div>
    ) : null;
  // While it is up, an empty grid's placeholder steps aside: the overlay's
  // text is centered right over it (the grids' dropOverlayActive).
  const dropOverlayActive = dropOverlay !== null;

  const handleStagePhoto = useCallback(
    (photoUUID: string) => {
      const photo = imageAssets.find((img) => img.photoUUID === photoUUID);
      if (photo) setStagingPhotos([photo]);
    },
    [imageAssets]
  );

  // DV-2856 (PR #1154 review, bug 3): this pool was unfiltered while the Stage
  // with AI button's count was query-narrowed, so a query typed after checking
  // sent the modal — and the customer's bill — photos the bar had stopped
  // counting. Both sides now read stageableSelection.
  const handleOpenBatchStaging = useCallback(() => {
    const stageable = stageableSelection(imageAssets, viewFilters);
    if (stageable.length > 0) setStagingPhotos(stageable);
  }, [imageAssets, viewFilters]);

  const handleDeletePhoto = useCallback(
    (photoUUID: string) => {
      const photo = imageAssets.find((img) => img.photoUUID === photoUUID);
      if (photo) setDeleteTarget([photo]);
    },
    [imageAssets]
  );

  // Hidden photos included on purpose — see assetViewScope#deletableSelection.
  // This is the one bulk action that honors them.
  //
  // DV-2856 (PR #1154 review, bug 2): the dialog took the raw checked list
  // while the bar's count was query-narrowed, so it could delete more photos
  // than it said. It now takes exactly the list the bar counted.
  const handleOpenDeleteSelected = useCallback(() => {
    const selected = deletableSelection(imageAssets, viewFilters);
    if (selected.length > 0) setDeleteTarget(selected);
  }, [imageAssets, viewFilters]);

  // DV-2221: the reorder persist call must submit an exact permutation of
  // the photoset's full backend order (including non-photo Zillow url
  // assets the grid never renders) — this is the merge base the grid's
  // drag-drop handler consumes via buildOrderedAssetUUIDs.
  const reorderContext = useMemo(
    () =>
      selectedPhotoset
        ? {
            fullAssetOrder: selectedPhotoset.assetUUIDs,
            photoAssetUUIDs: new Set(
              selectedPhotoset.assets.map((a) => a.assetUUID)
            )
          }
        : undefined,
    [selectedPhotoset]
  );

  if (isNotFound) {
    return (
      <PropertyNotFound
        currentOrgName={currentOrgName}
        onBackToProperties={isInternalEntry ? backAction : () => navigate(ROUTES.PROPERTIES)}
        entry={entry}
        onSwitchOrg={isInternalEntry ? undefined : () => navigate(initiateOrgSwitch())}
      />
    );
  }

  if (isLoadError) {
    return (
      <PropertyLoadError
        onRetry={retryPropertyFetch}
        onBackToProperties={isInternalEntry ? backAction : () => navigate(ROUTES.PROPERTIES)}
        entry={entry}
      />
    );
  }

  // DV-2849 PR 3 (spec §4.6). The hook raises these on the internal entry
  // only. A missing route photoset shows its own hero — never the workspace
  // around a neighboring set — and a failed photosets fetch retries the photos,
  // not the property.
  if (routePhotosetMissing) {
    return <PhotosetNotFound onBack={backAction} />;
  }

  if (assetLoadError) {
    return (
      <PropertyLoadError
        onRetry={retryAssetLoad}
        onBackToProperties={backAction}
        entry={entry}
        subject="photos"
      />
    );
  }

  if (!propertyMeta) {
    return (
      <div className="flex items-center justify-center h-full">
        <Spinner size="lg" />
      </div>
    );
  }

  // Staged items are optimistic-only (fake ids) — exclude them from anything
  // that reaches the backend (ordering, support requests, cart lookups).
  // TODO(DV-2252): drop the filter once the commit endpoint persists them.
  const backendSelectableImages = selectedImages.filter((img) => !img.staged);

  // If the picked service already covers exactly the selected photos in the
  // cart, open the modal in edit mode for that line (Update Cart / Remove)
  // rather than a blind re-add.
  const existingCartLine = pickedService
    ? getExactCartLineForSelection(
        pickedService.shoppingCartSlug,
        backendSelectableImages.map((img) => img.assetUUID)
      )
    : null;
  const orderServiceEdit = existingCartLine
    ? {
        // DV-2131 §3: prefer the stable lineItemUUID; lineAssetId stays as the legacy fallback.
        lineItemUUID: existingCartLine.lineItemUUID,
        lineAssetId: existingCartLine.assetId,
        initialNote: existingCartLine.additionalNotes || '',
        originalRefIds: existingCartLine.assetRefs?.length
          ? existingCartLine.assetRefs.map((r) => r.assetId)
          : [existingCartLine.assetId]
      }
    : undefined;

  // The control bar's desktop-only trailing slot. Photos: Internal PP's
  // Caption Sort button (DV-3537 — PP loses the service pills), otherwise the
  // pills. Zillow 3D renders the same control inert rather than removing it
  // (removing it caused an abrupt layout shift on tab switch). Other tabs,
  // Floor Plan included (DV-3735: no pills, no Caption Sort): none.
  const renderTrailingActions = () => {
    if (!isPhotosActive && !isZillowActive) return undefined;
    if (captionSortAsButton) {
      return (
        <CaptionSortButton
          onClick={captionSortPrompt.requestCaptionSort}
          disabled={isZillowActive || captionSortControlDisabled}
        />
      );
    }
    return (
      <AssetServicePills
        services={assetServiceList}
        // `+ Add Services` was dropped (spec §1.4 correction, 2026-08-19 PR A
        // QA round 2): desktop keeps these pills permanently rather than
        // replacing them. Disabled whenever nothing is selected, matching the
        // pool PhotosLeadingActions/PhotosMenuItems select from
        // (usePhotosActions' hidden-excluded selectedAssets).
        disabled={
          isZillowActive || bulkSelection(imageAssets, viewFilters).length === 0
        }
        onPickService={(service) => {
          setPickedFromServicesSheet(false);
          setOrderServiceExiting(false);
          setPickedService(service);
        }}
      />
    );
  };

  return (
    <>
      {/* Sized by the shell's dvh flex column, never by vh math. */}
      <section className="bg-background h-full min-h-0 flex flex-col">
        {/* DV-2861: hidden-from-client strip, above the header so it stays
            put while the scroll column below scrolls. */}
        {visibility.available && !visibility.visible && (
          <PhotosetVisibilityBanner onMakeVisible={visibility.openDialog} />
        )}
        {/* Header */}
        <header className="bg-card border-b border-border px-4 py-3 flex items-center justify-between gap-2 sticky top-0 z-10">
          {/* flex-1 min-w-0: the title block shrinks (wrapping internally on
              long addresses) instead of shoving the sync cluster onto its own
              wrapped row — the design keeps title and controls on ONE row at
              every width (QA round 3, 2026-08-19). */}
          <div className="flex flex-1 min-w-0 items-center gap-2">
            {/* shrink-0: the shadcn Button base class has none, so on a phone
                this lost the width fight against the header row and
                measured 20x44 — under the 44px touch floor the viewport suite
                asserts. Kept from the 2026-08-13 header work, which was
                otherwise reverted pending the Unit 4 assets redesign. */}
            <Button
              variant="ghost"
              size="icon"
              type="button"
              onClick={backAction}
              tooltip={backTooltip}
              className="shrink-0"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="min-w-0">
              {/* DV-2849 PR 2: read-only account context on the internal
                  workspace (null on /home, whose shell names the account).
                  Source text as stored, display casing by CSS. truncate: a
                  long name yields, never the sync cluster. */}
              {account.name && (
                <Typography
                  as="p"
                  variant="small-semibold"
                  className="truncate uppercase tracking-wide"
                >
                  <span className="sr-only">Account: </span>
                  {account.name}
                </Typography>
              )}
              {/* Two-line address (design 2026-08-19 round 4): street + unit
                  on the title line, locality below in muted text. Sub-lg the
                  street steps down to the heading-md scale step (the design's
                  ~17px semibold title; heading-lg's 24px dominated the phone
                  header — QA round 3, 2026-08-19). */}
              <Typography
                as="h1"
                variant="heading-lg"
                className="font-medium max-lg:text-base max-lg:leading-6 max-lg:font-semibold"
              >
                {propertyMeta.streetLine}
              </Typography>
              {/* body (14px), not small (12px): 12px read as too small at
                  desktop next to the heading-lg title (Steffon, QA round 4
                  follow-up). */}
              {propertyMeta.localityLine && (
                <Typography
                  as="p"
                  variant="body"
                  className="text-muted-foreground"
                >
                  {propertyMeta.localityLine}
                </Typography>
              )}
            </div>
          </div>
          {/* Right cluster per the design: the sync-state chip sits directly
              left of the Sync button, not under the address. DV-2820
              removed the last-synced line that used to sit under the address
              (explicit product decision). The timestamp survives only as the
              chip's tooltip, and that chip renders only for properties with
              an active sync channel (webhooks or propertySource 'Sync') — a
              property synced in the PAST with no channel today shows the
              timestamp nowhere. Known gap, recorded in the DV-2820 spec.

              Single inline row at every width (2026-08-19 design-update
              correction, spec §1.1 — supersedes the earlier sub-lg
              three-line/order-first stack): the chip collapses to an
              icon-only circle and the Sync and Share buttons collapse to
              icon-only sub-lg, so they fit alongside the address. shrink-0
              (with the title block's flex-1 min-w-0) guarantees the cluster
              never wraps or gets squeezed — the address yields instead.

              DV-3714 split the old Sync & Share button in two (Header
              Actions 2b): Sync, then the split Share control. */}
          <div className="flex shrink-0 items-center gap-4 max-lg:gap-2">
            {/* `!loading` guards against a synced-green flash: until the
                photoset fetch resolves, the persisted unsynced flags read
                as false and the chip would briefly claim "Synced" on a
                photoset that isn't. `!pmsStatusPending` is the same guard
                for the PMS side, whose read the photos no longer wait for
                (DV-3345, spec §5.2(2)). */}
            {!loading && !pmsStatusPending && showSyncStateNote && (
              <SyncStatusChip
                showBadge={isUnsynced}
                unsyncedCount={deliverableDirtyCount}
                pmsSource={pmsSource}
                lastSyncedTooltip={lastSynced}
                isSyncing={isSyncing}
                onClick={() => setShowSyncModal(true)}
              />
            )}
            {/* Always visible AND always enabled (DV-2750) — except for roles
                without Sync (DV-3519, canSync): syncing covers the whole
                photoset regardless of selection, and even a no-change sync is valid
                (backend confirms + stamps lastPmsSyncedAt). Properties without a PMS
                sync or webhook endpoint still get the modal's disabled-state
                messaging on the Webhooks tab. aria-label carries the accessible
                name once the label text hides sub-lg. */}
            {canSync && (
              <Button
                onClick={() => setShowSyncModal(true)}
                aria-label="Sync"
                title={syncButtonTitle(
                  showSyncButton ? pmsSource : null,
                  account.hasWebhookEndpoints
                )}
                className={cn('shrink-0', syncButtonPulse ? 'po-sync-button-pulse' : undefined)}
                onAnimationEnd={() => setSyncButtonPulse(false)}
              >
                <RefreshCw className="h-4 w-4" />
                <span className="max-lg:hidden">Sync</span>
              </Button>
            )}
            {/* Internal Share and the PlanOtour link (DV-3714). When
                it shows and whether the gallery link can be switched on is
                shareGate's call (tested there); hidden until launch by its
                release flag, which says who sees it on each backend. Keyed
                by photoset: another set's link starts from a fresh control. */}
            {shareShown && (
              <ShareControl
                key={share.photosetId}
                photosetId={share.photosetId}
                accountName={propertyMeta?.entity ?? null}
                token={selectedPhotoset?.publicToken ?? null}
                onTokenChange={(token) => setPhotosetPublicToken(share.photosetId, token)}
                galleryLink={share.galleryLink}
                modalOpen={showShareModal}
                onModalOpenChange={setShowShareModal}
              />
            )}
          </div>
        </header>

        {showOtherOrderNotice && (
          <Alert
            variant="warning"
            layout="row"
            className="mx-4 mt-3 w-auto"
            data-testid="photoset-other-order-notice"
          >
            <TriangleAlert className="h-4 w-4" aria-hidden="true" />
            <AlertDescription>
              This photoset was created for a different order on this property.
              Photos uploaded here are filed under that order. To give this
              order its own photoset, use Link Photoset (or Create Photoset
              while it is not yet delivered) from Global Orders.
            </AlertDescription>
          </Alert>
        )}

        {loading ? (
          <div className="flex items-center justify-center flex-1">
            <Spinner size="lg" />
          </div>
        ) : (
          <div
            ref={setScrollColumn}
            data-testid="assets-scroll-column"
            className="flex flex-1 flex-col overflow-y-auto"
          >
            {/* Unit 4 PR A (spec §1.3, corrected 2026-08-19): AssetsControlBar
                renders a tab row (tabs + Request Support) and a control row
                below it on every tab, replacing the separate tab-row +
                AssetsActionBar pair the original design merged into one row.
                Every tab feeds the control row's slots (Task 8): Photos gets
                leading actions/service pills/kebab, staged gets leading
                actions/kebab, trash gets a leading item-count summary only.
                Zillow 3D gets the SAME slots as Photos, all disabled rather
                than withheld (QA-round fix: removing them outright caused an
                abrupt layout shift when switching tabs) -- the photoset
                select and Request Support live on this one bar for every
                tab regardless. */}
            <AssetsControlBar
              tabs={tabs}
              activeTab={effectiveTab}
              onSelectTab={selectTab}
              collapseControlRow={scrollDirection === 'down' && !searchExpanded}
              onOpenSupportRequest={() => setShowAssetRequestModal(true)}
              hasPhotoset={hasPhotoset}
              photoSetOptions={photoSetOptions}
              selectedPhotosetId={selectedPhotosetId}
              onPhotoSetChange={handlePhotoSetChange}
              leadingActions={
                isPhotosActive || isFloorPlanActive ? (
                  <PhotosLeadingActions
                    setShowDownloadModal={(show) =>
                      setDownloadTarget(show ? getDownloadScope() : null)
                    }
                    onOpenUpload={openPhotosTabUpload}
                  />
                ) : isZillowActive ? (
                  <PhotosLeadingActions
                    setShowDownloadModal={(show) =>
                      setDownloadTarget(
                        show
                          ? getDownloadScope()
                          : null
                      )
                    }
                    onOpenUpload={canUploadHere ? handleOpenUpload : undefined}
                    disabled
                  />
                ) : isStagedActive ? (
                  <StagedLeadingActions
                    setShowDownloadModal={(show) =>
                      setDownloadTarget(
                        show
                          ? {
                              selection: getStagedDownloadSelection(),
                              // Hidden staged photos are internal unapproved
                              // WIP (DV-2820): a customer's download must not
                              // offer an include-hidden opt-in over drafts
                              // the grid withholds from them.
                              hiddenPhotoUUIDs: isAssetTeamUser
                                ? imageAssets
                                    .filter((img) => img.staged && img.hidden)
                                    .map((img) => img.photoUUID)
                                : []
                            }
                          : null
                      )
                    }
                    onOpenUpload={handleOpenStagedUpload}
                  />
                ) : isTrashActive ? (
                  <TrashSummary itemCount={trashedAssets.length} />
                ) : undefined
              }
              trailingActions={renderTrailingActions()}
              // DV-2856 (D5): Photos and Virtual Staging search; Zillow 3D
              // renders the field inert like its other controls (removing them
              // outright caused an abrupt layout shift on tab switch); Trash
              // gets none — it is a different data source with its own listing
              // query, and the AC scopes search away from it.
              search={
                isPhotosActive || isFloorPlanActive || isStagedActive ? (
                  <AssetsSearchField
                    expanded={searchExpanded}
                    onExpandedChange={setSearchExpanded}
                    locked={filtersLocked}
                  />
                ) : isZillowActive ? (
                  <AssetsSearchField
                    expanded={false}
                    onExpandedChange={() => {}}
                    disabled
                  />
                ) : undefined
              }
              searchExpanded={searchExpanded}
              // Decided 2026-09-18: a selection freezes the view filters, so
              // the pool can never move under it. Selection is per-tab-visit,
              // so "anything checked" is always this tab's selection.
              filtersLocked={filtersLocked}
              // Trash's menu is made entirely of sub-lg-only items, so its
              // kebab trigger must not render at lg+ -- otherwise desktop gets
              // a kebab whose popup is an empty box (final-review finding I1).
              // Virtual Staging LEFT this set in DV-2856: its menu now carries
              // Show Image Details, which has no desktop equivalent elsewhere
              // in the bar, so its trigger has to render at lg+ too. Zillow's
              // kebab trigger is ENABLED as of QA round 3 (2026-08-19): its
              // content renders inert per-item instead (PhotosMenuItems
              // `disabled`), so the sub-lg-first Request Support entry stays
              // reachable there.
              menuSubLgOnly={isTrashActive}
              menu={
                isPhotosActive || isFloorPlanActive ? (
                  <PhotosMenuItems
                    setShowDownloadModal={(show) =>
                      setDownloadTarget(show ? getDownloadScope() : null)
                    }
                    onOpenSupportRequest={() => setShowAssetRequestModal(true)}
                    onOpenUpload={openPhotosTabUpload}
                    visibility={visibilityMenuItem}
                    // DV-3735: no Caption Sort on Floor Plan (Tim, 2026-10-27).
                    captionSort={isFloorPlanActive ? undefined : captionSortMenuItem}
                  />
                ) : isZillowActive ? (
                  // Same handlers as the Photos branch -- every item renders
                  // disabled except Request Support (QA round 3).
                  <PhotosMenuItems
                    setShowDownloadModal={(show) =>
                      setDownloadTarget(
                        show
                          ? getDownloadScope()
                          : null
                      )
                    }
                    onOpenSupportRequest={() => setShowAssetRequestModal(true)}
                    onOpenUpload={canUploadHere ? handleOpenUpload : undefined}
                    visibility={visibilityMenuItem}
                    captionSort={captionSortMenuItem}
                    disabled
                  />
                ) : isStagedActive ? (
                  <StagedMenuItems
                    setShowDownloadModal={(show) =>
                      setDownloadTarget(
                        show
                          ? {
                              selection: getStagedDownloadSelection(),
                              hiddenPhotoUUIDs: isAssetTeamUser
                                ? imageAssets
                                    .filter((img) => img.staged && img.hidden)
                                    .map((img) => img.photoUUID)
                                : []
                            }
                          : null
                      )
                    }
                    onOpenSupportRequest={() => setShowAssetRequestModal(true)}
                    onOpenUpload={handleOpenStagedUpload}
                  />
                ) : isTrashActive ? (
                  // Trash has no bulk kebab actions of its own; sub-lg it
                  // still needs the Request Support entry every other tab
                  // carries (QA round 3, 2026-08-19). menuSubLgOnly above
                  // keeps desktop trash kebab-free (Request Support lives in
                  // the tab row there).
                  <RequestSupportMenuItem
                    onOpenSupportRequest={() => setShowAssetRequestModal(true)}
                  />
                ) : undefined
              }
            />

            {/* Tab content. The action bar sits outside the padded wrapper so
                its sticky/full-width chrome spans edge-to-edge; only the grid
                (and the Zillow panel) take the p-6 inset. */}
            {isZillowActive ? (
              <>
                {/* ZillowAssets is a self-contained "here's your tour link"
                    card, not a select/bulk-action row like the other three
                    views -- but AssetsControlBar above still renders the
                    Photos-shaped controls for this tab, disabled rather than
                    withheld (QA-round fix: removing them caused an abrupt
                    layout shift on tab switch). The photoset select is
                    carried by AssetsControlBar for every tab regardless, and
                    Request Support stays reachable here too — desktop via the
                    tab row, sub-lg via the kebab's first entry (spec §1.3 +
                    QA round 3, 2026-08-19). */}
                <div className="p-6">
                  <ZillowAssets zillow3d={zillow3d} />
                </div>
              </>
            ) : isTrashActive ? (
              <>
                {/* No dropOverlay, and resolveDropTarget returns null for this
                    tab: dropping files onto the Trash must not start an
                    upload. */}
                <div className="flex-1 p-6">
                  <TrashGrid
                    assets={trashedAssets}
                    isLoading={trashLoading}
                    isError={trashError}
                    onRetry={refetchTrash}
                    onRestore={recoverPhoto}
                    onPurge={setPurgeTarget}
                    busyAssetUUID={busyAssetUUID}
                  />
                </div>
              </>
            ) : isStagedActive ? (
              <>
                <div className="relative flex-1 p-6">
                  <StagedAssetsGrid
                    onImageClick={handleStagedImageClick}
                    onDownload={handleDownloadPhoto}
                    onStage={
                      canOpenStagingModal ? handleStagePhoto : undefined
                    }
                    dropOverlayActive={dropOverlayActive}
                  />
                  {dropOverlay}
                </div>
              </>
            ) : (
              <>
                <div className="relative flex-1 p-6">
                  {photosetUnavailable ? (
                    <PhotosetUnavailable />
                  ) : (
                    <AssetsImageGrid
                      onImageClick={handleImageClick}
                      onOpenUpload={openPhotosTabUpload}
                      // The grid withholds it from floor plans (canStagePhoto),
                      // on Photos as on the Floor Plan tab.
                      onStage={
                        canOpenStagingModal ? handleStagePhoto : undefined
                      }
                      onDownloadPhoto={handleDownloadPhoto}
                      onDelete={canDelete ? handleDeletePhoto : undefined}
                      reorderContext={reorderContext}
                      dropOverlayActive={dropOverlayActive}
                    />
                  )}
                  {dropOverlay}
                </div>
              </>
            )}

            {/* Floating selection bar (2026-08-19 design update, spec §1.10),
                Photos and Floor Plan tabs only. A CHILD OF THE SCROLL COLUMN on purpose: its
                `sticky bottom-4` pins against this container, and as the last
                child it floats over the grid while scrolling and comes to rest
                at the column's end. It must never be absolute-anchored to the
                app shell's <main>, which carries `relative` for three other
                absolute-rooted page roots (roadmap Known hazards). Renders
                itself null unless a photo is checked. */}
            {(isPhotosActive || isFloorPlanActive) && (
              <AssetsSelectionBar
                // DV-3735: no services on Floor Plan — the AC drops its pills,
                // and this is their sub-lg twin.
                services={isFloorPlanActive ? [] : assetServiceList}
                servicesSheetOpen={servicesSheetOpen}
                onServicesSheetOpenChange={setServicesSheetOpen}
                onPickService={(service) => {
                  setPickedFromServicesSheet(true);
                  // A pick always lands on an OPEN order sheet — including a
                  // re-pick while the previous back transition is still
                  // sliding out (the modal's !open guard drops that exit's
                  // late transitionend).
                  setOrderServiceExiting(false);
                  setPickedService(service);
                }}
                onOpenDownload={() => setDownloadTarget(getDownloadScope())}
                onOpenSupport={() => setShowAssetRequestModal(true)}
                // A floor plan can't be staged (canStagePhoto), so on its tab
                // the button could only ever be disabled: leave it out.
                onOpenStaging={
                  canOpenStagingModal && !isFloorPlanActive
                    ? handleOpenBatchStaging
                    : undefined
                }
                onOpenDelete={canDelete ? handleOpenDeleteSelected : undefined}
              />
            )}
          </div>
        )}
      </section>

      {/* Modals */}
      {uploadTarget && uploadContext && (
        <UploadModal
          ctx={uploadContext}
          title={`Upload ${UPLOAD_TARGET_NOUN[uploadTarget]}`}
          initialFiles={pendingDropFiles ?? undefined}
          onUploaded={handleUploaded}
          onClose={() => {
            setUploadTarget(null);
            setPendingDropFiles(null);
          }}
          onDiscardIncomplete={({ rejected, failed, leftovers }) => {
            // They are still in the photoset server-side, but the grid only ever
            // receives confirmed uploads: add them so the user can see and delete
            // them — only if the grid still shows the set they were uploaded to
            // (addUploaded skips any already there).
            const here = leftovers.filter(
              (image) => image.photosetUUID === selectedPhotosetIdRef.current
            );
            if (here.length > 0) dispatch(addUploaded(here));
            const left = rejected.length + failed.length;
            const photos = `${left} cancelled photo${left === 1 ? '' : 's'}`;
            const elsewhere = left - here.length;
            const where =
              elsewhere === 0
                ? `${left === 1 ? 'It is' : 'They are'} now in the grid; delete ${left === 1 ? 'it' : 'them'} there.`
                : here.length === 0
                  ? 'Open the photoset they were uploaded to and delete them there.'
                  : `${here.length} ${here.length === 1 ? 'is' : 'are'} now in the grid; delete ${here.length === 1 ? 'it' : 'them'} there. Open the photoset the other ${elsewhere} ${elsewhere === 1 ? 'was' : 'were'} uploaded to for the rest.`;
            dispatch(
              showAlert({
                type: Variants.WARNING,
                message: `${photos} could not be removed and ${left === 1 ? 'is' : 'are'} still in the photoset. ${where}`
              })
            );
          }}
        />
      )}
      {downloadTarget &&
        (() => {
          // Single-image downloads name the file after that image's grid
          // position + caption (buildImageFileName); multi-image downloads
          // fall back to the zip's own naming, so fallbackImageName is left
          // undefined. One scan of imageAssets finds both the photo and its
          // 1-based grid index.
          const singleDownloadIndex =
            downloadTarget.selection.photoUUIDs?.length === 1
              ? imageAssets.findIndex(
                  (img) =>
                    img.photoUUID === downloadTarget.selection.photoUUIDs?.[0]
                )
              : -1;
          const singleDownloadPhoto =
            singleDownloadIndex === -1
              ? undefined
              : imageAssets[singleDownloadIndex];
          return (
            <Modal
              open
              onOpenChange={(open) => !open && setDownloadTarget(null)}
              title="Download Assets"
              size="xl"
            >
              <DownloadAsset
                setShowModel={(show) => !show && setDownloadTarget(null)}
                selection={downloadTarget.selection}
                hiddenPhotoUUIDs={downloadTarget.hiddenPhotoUUIDs}
                watermarkAvailable={account.watermarkAvailable}
                zipNameBase={buildZipNameBase(
                  propertyMeta.address1,
                  propertyMeta.unitNumber,
                  propertyMeta.propertyAddress
                )}
                fallbackImageName={
                  singleDownloadPhoto
                    ? buildImageFileName(
                        singleDownloadIndex + 1,
                        singleDownloadPhoto.caption ?? null
                      )
                    : undefined
                }
              />
            </Modal>
          );
        })()}
      {showSyncModal && (
        <SyncModal
          open={showSyncModal}
          onOpenChange={setShowSyncModal}
          showSyncTab={showSyncButton}
          pmsSource={pmsSource}
          syncSelection={getSyncSelection()}
          setSyncFailure={setSyncFailure}
          onPmsSyncSuccess={handlePmsSyncSuccess}
          photosetId={selectedPhotosetId}
          photosetName={selectedPhotoset?.name ?? ''}
          lastWebhookTriggeredAt={
            selectedPhotoset?.lastWebhookTriggeredAt ?? null
          }
          hasWebhookEndpoints={account.hasWebhookEndpoints}
          watermarkAvailable={account.watermarkAvailable}
          photoCount={photoCount}
          onSyncSuccess={() => {
            if (selectedPhotosetId) markPhotosetSynced(selectedPhotosetId);
            // TODO(DV-2252): the sync webhook can't yet report staged photos
            // or staged-original hides, so clearing all dirty flags here
            // under-reports unsynced staging changes until the commit
            // endpoint lands and the webhook payload covers them.
            dispatch(clearDirtyFlags());
          }}
          isPaused={isPaused}
          pausedActions={pausedActions}
        />
      )}
      {syncFailure && pmsSource && (
        <FailedAssetSync
          failure={syncFailure}
          onClose={() => setSyncFailure(null)}
          propertyAddress={propertyMeta.propertyAddress}
          pmsSource={pmsSource}
          actions={failedActions}
        />
      )}
      <AssetRequestModal
        show={showAssetRequestModal}
        onHide={() => setShowAssetRequestModal(false)}
        onSupportSubmit={() => {}}
        assetData={backendSelectableImages}
        requestType="support"
        propertyAddress={propertyMeta.propertyAddress}
      />
      <AssetViewModal
        show={showImageViewModal}
        onHide={() => setShowImageViewModal(false)}
        servicesList={assetServiceList}
        imageList={viewerImagesByScope[viewerScope]}
        currentIndex={imageViewIndex}
        propertyAddress={propertyMeta.propertyAddress}
        onDownloadRequest={handleDownloadPhoto}
      />
      {pickedService && (
        <OrderServiceModal
          // `open` flips false while `pickedService` is still set only during
          // the back transition — the sheet slides down (vaul exit) while the
          // services sheet slides up, and onExited unmounts it after.
          open={!orderServiceExiting}
          service={pickedService}
          photos={backendSelectableImages}
          propertyAddress={propertyMeta.propertyAddress}
          edit={orderServiceEdit}
          onClose={() => {
            setPickedService(null);
            setPickedFromServicesSheet(false);
            setOrderServiceExiting(false);
          }}
          onBack={
            pickedFromServicesSheet
              ? () => {
                  setOrderServiceExiting(true);
                  setServicesSheetOpen(true);
                }
              : undefined
          }
          onExited={() => {
            // Fires on any sheet slide-down while still mounted, which only
            // the back path produces (plain close unmounts immediately).
            setPickedService(null);
            setOrderServiceExiting(false);
          }}
        />
      )}
      {stagingPhotos && (
        <StagingModal
          photos={stagingPhotos}
          onClose={() => setStagingPhotos(null)}
        />
      )}
      {deleteTarget && (
        <DeleteConfirmDialog
          mode="trash"
          photos={deleteTarget.map(
            (img): DeletePreviewPhoto => ({
              key: img.photoUUID,
              caption: img.caption ?? null,
              thumbnailUrl: pickThumbnailUrl(img.caches),
              hidden: img.hidden
            })
          )}
          onCancel={() => setDeleteTarget(null)}
          onConfirm={() => {
            const photos = deleteTarget;
            setDeleteTarget(null);
            void trashPhotos(photos);
          }}
        />
      )}
      {purgeTarget && (
        <DeleteConfirmDialog
          mode="purge"
          photos={[
            {
              key: purgeTarget.assetUUID,
              caption: purgeTarget.caption,
              thumbnailUrl: pickThumbnailUrl(purgeTarget.caches)
            }
          ]}
          onCancel={() => setPurgeTarget(null)}
          onConfirm={() => {
            const asset = purgeTarget;
            setPurgeTarget(null);
            void purgePhoto(asset);
          }}
        />
      )}
      {/* DV-3563: Make Visible offers the team email while sharing is
          released; Hide, and Make Visible without it, are DV-2861's. */}
      {visibility.dialog &&
        (shareReleased && visibility.dialog.target === 'visible' ? (
          <MakeVisibleNotifyDialog
            photosetId={visibility.dialog.photosetId}
            accountName={visibility.dialog.accountName}
            saving={visibility.dialog.saving}
            error={visibility.dialog.error}
            onConfirm={(notify) => void visibility.confirm(notify)}
            onCancel={visibility.cancel}
          />
        ) : (
          <PhotosetVisibilityDialog
            target={visibility.dialog.target}
            accountName={visibility.dialog.accountName}
            saving={visibility.dialog.saving}
            error={visibility.dialog.error}
            onConfirm={() => void visibility.confirm()}
            onCancel={visibility.cancel}
          />
        ))}
      {captionSortPrompt.dialog && (
        <CaptionSortMissingCaptionsDialog
          missingCount={captionSortPrompt.dialog.missingCount}
          onSortAnyway={captionSortPrompt.dialog.confirm}
          onGoBack={captionSortPrompt.dialog.cancel}
        />
      )}
      <UnsyncedLeaveDialog
        open={leaveBlocker.isBlocking}
        onStay={leaveBlocker.cancel}
        onLeave={leaveBlocker.proceed}
      />
    </>
  );
};

// One workspace per property. The routes carry no `key`, so without one React
// reuses the workspace when the URL's property changes, and everything it
// holds for the previous property survives into the next: usePropertyAssets'
// photosets and selected set, its already-loaded skip, the trash and dialog
// state. The photoset auto-redirect then puts the old property's set under the
// new property's URL, the load skips it as already loaded, and upload — which
// gates on a selected set (DV-2861) — lands in the old property's photoset.
// A photoset switch within one property keeps the instance; its no-flash
// switch depends on that.
const PropertyAssetManagementPage = (props: PropertyAssetManagementPageProps) => {
  const { propertyUUID } = useParams();
  return <PropertyAssetWorkspace key={propertyUUID} {...props} />;
};

export default PropertyAssetManagementPage;
